/**
 * Заведение заявки в банк через СНС из чата брокера (#436): распознавание намерения,
 * поиск банка/площадки в подборе и шаги сбора недостающих данных заявки.
 * Сама отправка — существующими эндпоинтами заявки (/selections/{ref}/submission|submit).
 */
import { BrokerOffer } from "../types";

// «подай/заведи/оформи/подготовь/создай заявку …», «заявку в ПСБ», «подать через СНС».
// Глаголов больше, чем кажется нужным: брокер пишет «подготовь заявку», «создай заявку»,
// «сделай заявку» — и каждое непонятое уходило в модель, которая отвечала инструкцией
// «подготовьте заявку» вместо действия (#615).
const SUBMIT_VERB = "(пода(й|йте|ть|м)|завед(и|ите|ём|ем)|заве(сти|дём)|оформ(и|ите|ить)"
  + "|отправ(ь|ьте|ить)|подготов(ь|ьте|им|ить)|созда(й|йте|ть|м)|сдела(й|йте|ть|м))";
const SUBMIT_RE = new RegExp(
  `${SUBMIT_VERB}\\s.{0,40}заявк|заявк\\S*\\s+(в|через|к)\\s|через\\s+снс|${SUBMIT_VERB}\\s+(через|в|на)\\s`,
  "i");

/** Короткая побудительная реплика без объекта: «так подготовь мне», «давай», «сделай».
 *
 *  Брокер не повторяет условие, которое только что назвал: сказав «подготовь заявку в
 *  самые дешёвые банки» и не получив действия, он пишет «так подготовь мне». Без этого
 *  реплика уходила в модель и возвращалась отказом не по делу (#615). */
export function isDoItIntent(text: string): boolean {
  const t = text.trim().toLowerCase().replace(/[!.]+$/, "");
  if (t.split(/\s+/).length > 4) return false;
  return /^(так\s+)?(подготов|сдела|созда|завед|оформ|давай|погнали|действуй|выполн)/.test(t);
}

/** «добавь ещё банки», «добавь остальные» — расширить уже начатую заявку (#615). */
export function isAddBanksIntent(text: string): boolean {
  return /(добав|включ|ещё|еще|остальн|остальные)/i.test(text) && /банк/i.test(text);
}

/** «в самые дешёвые», «в топ-3», «во все проходящие» — выбор банков по признаку, а не по
 *  имени (#615). Брокер так и просит: «подготовь заявку в самые дешёвые банки». */
export function bulkSnsPick(text: string): { kind: "cheap" | "kv" | "all"; limit: number } | null {
  const t = text.toLowerCase();
  if (!/банк/.test(t)) return null;
  const m = /топ[-\s]?(\d+)/.exec(t);
  const limit = m ? Math.max(1, Math.min(Number(m[1]), 5)) : 3;
  if (/(дешёв|дешев|выгодн|ниже\s+цен|минимальн)/.test(t)) return { kind: "cheap", limit };
  if (/(кв|вознагражд)/.test(t) && /(больш|выше|максим)/.test(t)) return { kind: "kv", limit };
  if (/(во\s+все|всем|все\s+проход|всех\s+проход)/.test(t)) return { kind: "all", limit: 99 };
  if (m) return { kind: "cheap", limit };
  return null;
}

export function isSnsSubmitIntent(text: string): boolean {
  return SUBMIT_RE.test(text) && !isSnsListIntent(text);
}

/** Вопрос о подключении к СНС: «какие банки подключены к СНС», «куда можно подать
 *  автоматически», «Сбер подключён к СНС?». Отвечаем живой проверкой, не LLM (#436). */
export function isSnsListIntent(text: string): boolean {
  return /снс|автоматическ/i.test(text) && /(каки|какой|кто|где|(^|\s)ли(\s|$)|список|\?|подключ)/i.test(text);
}

/** Короткое уточнение вслед за вопросом о СНС: «а Сбербанк?» → «Сбербанк». null — не оно. */
export function followUpBankName(text: string): string | null {
  const t = text.trim().replace(/[?!.]+$/, "").replace(/^(а|и|ну|ещё|еще)\s+/i, "").trim();
  if (!t || t.split(/\s+/).length > 3 || /\d/.test(t)) return null;
  return t;
}

const norm = (s: string) => s.toLowerCase().replace(/ё/g, "е").replace(/[«»"'().,\-]/g, " ")
  // \b в JS не видит кириллицу — границы слова задаём явно.
  .replace(/(?<![а-яa-z0-9])(пао|ао|оао|зао|ооо|акб|кб|банк|bank)(?![а-яa-z0-9])/g, " ").replace(/\s+/g, "");

// «Промсвязьбанк» в тексте → и «промсвязьбанк», и разговорное «псб»; «Тендертех» → площадка.
const ALIASES: Record<string, string[]> = { "промсвязь": ["псб"], "совкомбанк": ["совком"], "газпромбанк": ["гпб"] };

function mentions(textNorm: string, name?: string | null): boolean {
  const n = norm(name || "");
  if (n.length < 3) return false;
  if (textNorm.includes(n)) return true;
  return Object.entries(ALIASES).some(([k, al]) => n.includes(k) && al.some((a) => textNorm.includes(a)));
}

export interface SnsTarget {
  offer: BrokerOffer;          // предложение, по которому заводим (банк + площадка)
  byPlatform: boolean;         // брокер назвал площадку, а не банк
}

/** Какое предложение подбора имеет в виду брокер. Площадка сужает до её банков; среди
 *  нескольких — проходящее и самое дешёвое. null — в тексте нет ни банка, ни площадки. */
export function findSnsTarget(text: string, offers: BrokerOffer[]): SnsTarget | null {
  const t = norm(text);
  const byBank = offers.filter((o) => mentions(t, o.bank));
  const byPlat = offers.filter((o) => mentions(t, o.platform));
  let pool = byBank.length && byPlat.length ? byBank.filter((o) => byPlat.includes(o)) : [];
  if (!pool.length) pool = byBank.length ? byBank : byPlat;
  if (!pool.length) return null;
  const best = [...pool].sort((a, b) =>
    Number(b.eligible) - Number(a.eligible) || (a.commission || 0) - (b.commission || 0))[0];
  return { offer: best, byPlatform: !byBank.length && byPlat.length > 0 };
}

/** Назвал ли брокер площадку («подай в ПСБ на Теледок»). */
export function mentionsPlatform(text: string, offers: BrokerOffer[]): boolean {
  const t = norm(text);
  return offers.some((o) => mentions(t, o.platform));
}

/** Банк, о котором шла речь последним («подай заявку в него»): ищем по репликам с конца. */
export function lastMentionedOffer(texts: string[], offers: BrokerOffer[]): BrokerOffer | null {
  for (const s of [...texts].reverse()) {
    const hit = findSnsTarget(s, offers);
    if (hit) return hit.offer;
  }
  return null;
}

// ── Шаги сбора данных заявки ────────────────────────────────────────────────

export interface SnsField {
  key: string;
  label: string;
  ask: string;
  required: boolean;
  /** Нормализует ввод; null — не прошло проверку (переспросим с подсказкой hint). */
  parse: (v: string) => string | null;
  hint: string;
}

const digits = (v: string) => v.replace(/\D/g, "");

function parsePhone(v: string): string | null {
  let d = digits(v);
  if (d.length === 11 && d.startsWith("8")) d = "7" + d.slice(1);
  if (d.length === 10) d = "7" + d;
  return d.length >= 11 && d.length <= 15 ? `+${d}` : null;
}

function parseDate(v: string): string | null {
  const m = v.trim().match(/^(\d{1,2})[.\-/](\d{1,2})[.\-/](\d{4})$/) || null;
  if (m) return `${m[3]}-${m[2].padStart(2, "0")}-${m[1].padStart(2, "0")}`;
  return /^\d{4}-\d{2}-\d{2}$/.test(v.trim()) ? v.trim() : null;
}

export const SNS_FIELDS: SnsField[] = [
  { key: "contact_name", label: "Контактное лицо", required: true,
    ask: "ФИО контактного лица клиента (с ним будет связываться банк)?",
    parse: (v) => (v.trim().length >= 3 ? v.trim() : null), hint: "Нужно ФИО, например «Иванов Иван Иванович»." },
  { key: "contact_phone", label: "Телефон", required: true,
    ask: "Телефон контактного лица?",
    parse: parsePhone, hint: "Телефон — 10–15 цифр, например +7 999 123-45-67." },
  { key: "contact_email", label: "Email", required: false,
    ask: "Email контактного лица? (можно «пропустить»)",
    parse: (v) => (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()) ? v.trim() : null), hint: "Email вида name@company.ru." },
  { key: "bank_bic", label: "БИК банка клиента", required: true,
    ask: "Реквизиты счёта клиента (банк без них заявку не примет): БИК банка?",
    parse: (v) => (digits(v).length === 9 ? digits(v) : null), hint: "БИК — 9 цифр." },
  { key: "payment_account", label: "Расчётный счёт", required: true,
    ask: "Расчётный счёт клиента?",
    parse: (v) => (digits(v).length === 20 ? digits(v) : null), hint: "Расчётный счёт — 20 цифр." },
  { key: "correspondent_account", label: "Корр. счёт", required: true,
    ask: "Корреспондентский счёт банка клиента?",
    parse: (v) => (digits(v).length === 20 ? digits(v) : null), hint: "Корр. счёт — 20 цифр." },
  { key: "date_end", label: "Окончание гарантии", required: true,
    ask: "Дата окончания гарантии (ДД.ММ.ГГГГ)?",
    parse: parseDate, hint: "Дата в формате ДД.ММ.ГГГГ, например 31.12.2027." },
];

export const isSkip = (v: string) => /^(пропуст|нет|-|—|skip|дальше)/i.test(v.trim());
export const isCancel = (v: string) => /^(отмен|стоп|не надо|выйти|cancel)/i.test(v.trim());

/** Следующее незаполненное поле. asked — уже спрошенные в этом проходе (пропущенные
 *  необязательные повторно не спрашиваем). */
export function nextField(sub: Record<string, any>, asked: Set<string>): SnsField | null {
  return SNS_FIELDS.find((f) => !sub[f.key] && !asked.has(f.key)) || null;
}

/** Тип документа по имени файла (как в форме СНС): паспорт/устав/решение/отчётность. */
/** Порядок проверки типов: узкие раньше широких (#614).
 *
 *  «Паспорт учредителя» обязан обойти «паспорт», а «отчётность за 1 квартал» — годовую,
 *  иначе файл уедет не в тот тип. Тот же порядок на бэкенде (`sns.doc_type_by_name`). */
const DOC_TYPE_ORDER = ["buh_q", "founder_passport", "passport", "ustav", "decision",
                        "shareholders", "lease", "auction", "buh"];

/** Тип документа по имени файла.
 *
 *  Правила приходят с бэкенда (`docTypeMatch` в состоянии заявки) — один справочник на
 *  анкету и на диалог. Раньше их было два, с разными словами: «Реестр акционеров.pdf» в
 *  анкете определялся верно, а в чате становился «прочим документом».
 *
 *  Правил ещё нет (состояние не загрузилось) — возвращаем `other`: пусть брокер поправит
 *  тип руками, это честнее, чем угадать своим набором слов и разойтись с анкетой. */
export function docTypeByName(fileName: string, match?: Record<string, string[]>): string {
  const n = (fileName || "").toLowerCase();
  const rules = match || {};
  for (const code of DOC_TYPE_ORDER) {
    if ((rules[code] || []).some((w) => n.includes(w))) return code;
  }
  return "other";
}

export const fmtDate = (iso?: string | null) =>
  iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "—";
